import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthContext';
import { authApi } from '@/api/auth';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import confetti from 'canvas-confetti';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshSubscription } = useAuth();
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const checkStatus = async () => {
      const sessionId = searchParams.get('session_id');
      
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const result = await authApi.checkPaymentStatus(sessionId);
        setPaymentDetails(result);
        
        if (result.payment_status === 'paid') {
          setStatus('success');
          // Refresh subscription data
          await refreshSubscription();
          // Fire confetti!
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
        } else {
          setStatus('pending');
        }
      } catch (error) {
        setStatus('error');
      }
    };

    checkStatus();
  }, [searchParams, refreshSubscription]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-black/40 border border-white/10 rounded-xl p-8 max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-blue-400 animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-2">Processing Payment...</h2>
            <p className="text-gray-400">Please wait while we confirm your payment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-green-400">Payment Successful!</h2>
            <p className="text-gray-400 mb-6">
              Thank you for upgrading! Your new plan is now active.
            </p>
            {paymentDetails && (
              <div className="bg-white/5 rounded-lg p-4 mb-6 text-left">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">Amount Paid</span>
                  <span className="font-medium">
                    ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                  </span>
                </div>
              </div>
            )}
            <Button onClick={() => navigate('/')} className="w-full">
              Go to Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        )}

        {status === 'pending' && (
          <>
            <Loader2 className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-2 text-yellow-400">Payment Processing</h2>
            <p className="text-gray-400 mb-6">
              Your payment is being processed. This may take a few moments.
            </p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Refresh Status
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">❌</span>
            </div>
            <h2 className="text-2xl font-bold mb-2 text-red-400">Something Went Wrong</h2>
            <p className="text-gray-400 mb-6">
              We couldn't verify your payment. Please contact support if you were charged.
            </p>
            <Button onClick={() => navigate('/pricing')} variant="outline">
              Back to Pricing
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
